import { useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest, setImpersonationGuardHandler, isHighRiskAction } from "@/lib/queryClient";
import { AlertTriangle, X } from "lucide-react";

type VendorImpersonationStatus = {
  active: boolean;
  expired?: boolean;
  vendorId?: number;
  vendorName?: string | null;
  vendorTeamMemberId?: number | null;
  teamMemberName?: string | null;
  expiresAt?: number;
  sessionId?: number;
};

export function VendorImpersonationBanner() {
  const statusQuery = useQuery<VendorImpersonationStatus>({
    queryKey: ["/api/app/impersonation/vendor/status"],
    queryFn: async () => {
      const res = await fetch("/api/app/impersonation/vendor/status", { credentials: "include" });
      if (!res.ok) return { active: false };
      return res.json();
    },
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

  const endMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      await apiRequest("POST", "/api/app/impersonation/vendor/end");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/impersonation/vendor/status"] });
      queryClient.clear();
      window.location.assign("/app/dev-tools/impersonate");
    },
  });

  const status = statusQuery.data;
  const isActive = !!status?.active;

  useEffect(() => {
    if (isActive) {
      setImpersonationGuardHandler((method: string, url: string) => {
        if (!isHighRiskAction(method, url)) return Promise.resolve(true);
        return new Promise<boolean>((resolve) => {
          const confirmed = window.confirm(
            `⚠ Vendor Impersonation Write Confirmation\n\nYou are acting as vendor "${status?.vendorName}"${status?.teamMemberName ? ` (member: ${status.teamMemberName})` : ""}.\n\nThis action will affect real data. Proceed?`,
          );
          resolve(confirmed);
        });
      });
    } else {
      setImpersonationGuardHandler(null);
    }
    return () => {
      setImpersonationGuardHandler(null);
    };
  }, [isActive, status?.vendorName, status?.teamMemberName]);

  if (!isActive || !status?.vendorName) return null;

  const { vendorName, teamMemberName, expiresAt } = status;
  const minutesLeft = expiresAt ? Math.max(0, Math.floor((expiresAt - Date.now()) / 60000)) : 0;

  return (
    <div
      className="w-full bg-amber-500 text-white px-4 py-2 flex items-center gap-3 text-sm font-medium z-50"
      data-testid="banner-vendor-impersonation"
    >
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <span className="flex-1">
        ⚠ Impersonating vendor <strong>{vendorName}</strong>
        {teamMemberName && <span className="font-normal"> · member: <strong>{teamMemberName}</strong></span>}
        {minutesLeft > 0 && <span className="font-normal text-amber-100 ml-2">· {minutesLeft}m remaining</span>}
      </span>
      <button
        onClick={() => endMutation.mutate()}
        disabled={endMutation.isPending}
        className="flex items-center gap-1.5 bg-white text-amber-700 px-3 py-1 rounded text-xs font-semibold hover:bg-amber-50 transition-colors"
        data-testid="button-exit-vendor-impersonation"
      >
        <X className="h-3 w-3" />
        Exit Impersonation
      </button>
    </div>
  );
}
